import { useEffect, useState } from "react";
import LubbockHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Lubbock?", a: "We offer Lubbock-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department processing volumes. We review your documents before submission to catch errors that cause delays." },
  { q: "Do I need to visit an office in Lubbock?", a: "No. We do not have a physical office in Lubbock. Our service is remote: you complete forms online, visit a local acceptance facility (like a Lubbock post office or county clerk) for in-person verification, then ship documents to us via overnight courier. We handle the expedited submission at the State Department and return your passport to your Lubbock address." },
  { q: "Where is the nearest passport acceptance facility in Lubbock?", a: "Lubbock has several acceptance facilities including post offices (downtown, 4th Street, South Loop), the Lubbock County Clerk's office, and select library locations. We provide a curated list of Lubbock-area facilities with address, phone, and photo availability. First-time applicants and minors must visit in person; renewals typically ship directly." },
  { q: "Can I get a same-day passport in Lubbock?", a: "There is no passport agency in Lubbock — the nearest is in Dallas, over 300 miles away. Same-day processing is not available locally. For urgent needs, our registered courier service offers the fastest legally available channel — as quick as 24 hours with proper documentation, without you needing to drive to Dallas." },
  { q: "How much does expedited passport service cost in Lubbock?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee that covers document review, overnight shipping, and expedited submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
  { q: "What documents do I need for a Lubbock passport application?", a: "For a new adult passport: proof of citizenship (certified birth certificate or naturalization certificate), government-issued photo ID, one passport photo, and the completed DS-11 form. For renewals: your most recent passport, one photo, and the DS-82 form. For minors: both parents must appear (or provide consent), plus the child's birth certificate and photos. We provide a detailed checklist for Lubbock clients." },
  { q: "Can I get a passport photo in Lubbock?", a: "Yes. Many Lubbock post offices, CVS, Walgreens, and Walmart locations offer passport photos. Some post offices include photo services with their acceptance facility hours. We recommend confirming photo availability before visiting. Passport photos must meet strict State Department specifications — 2x2 inches, white background, no glasses, neutral expression." },
  { q: "Do I need an appointment at Lubbock passport acceptance facilities?", a: "Many Lubbock post offices require appointments for passport acceptance. Some accept walk-ins during limited hours. We recommend calling ahead. The Lubbock County Clerk's office and larger post offices typically offer more flexible appointment times. We can help you find the facility with the next available appointment." },
  { q: "Can I track my Lubbock passport application?", a: "Yes. Once we submit your application, we provide tracking information. You can also check the State Department's online passport status system. Our courier service includes tracking at every stage: from Lubbock to our processing center, through State Department review, and return shipping to your Texas address." },
  { q: "What if my passport is lost or stolen in Lubbock?", a: "File a police report if stolen, then complete Form DS-64 (Statement Regarding Lost or Stolen Passport) along with Form DS-11 for a replacement. You will need to visit an acceptance facility in person and provide citizenship documentation again. If you have upcoming travel, our expedited service can process the replacement as fast as 24 hours." },
];

function LubbockStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) {
            setActive(id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" },
    { id: "services", label: "Services" },
    { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" },
    { id: "destinations", label: "Destinations" },
    { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" },
    { id: "comparison", label: "Compare" },
    { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" },
    { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a
            key={l.id}
            href={`#${l.id}`}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${
              active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"
            }`}
          >
            {l.label}
          </a>
        ))}
      </div>
    </div>
  );
}

function LubbockProcessingTimeSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Lubbock Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">We offer multiple speed tiers for Lubbock-area travelers. Choose the timeline that matches your departure urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Lubbock travelers with imminent departure within 3 days. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For Texas Tech students and business travelers with travel in 1–2 weeks. Balanced speed and value with full document review." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Lubbock residents planning ahead. Still faster than standard post office processing (10–13 weeks) with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LubbockServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Lubbock travelers with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Lubbock residents with a passport issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time application guidance for Lubbock adults with acceptance facility support." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Lubbock residents." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Lubbock families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-stack-line", title: "Second Passport", desc: "Second passport issuance for frequent Lubbock business travelers." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay Lubbock applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Lubbock Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Lubbock's diverse travel community — from Texas Tech students to cotton industry executives to West Texas oil workers.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LubbockLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Lubbock</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Lubbock is the heart of West Texas — a city of 260,000 people anchored by Texas Tech University's 40,000+ students, the vast cotton industry that drives the region's economy, and the oil and gas fields that stretch across the Permian Basin. The city's geographic isolation is its defining feature: Lubbock sits 300+ miles from Dallas, 350 miles from Houston, and over 400 miles from Austin. When a Lubbock resident needs a passport in a hurry, driving to the nearest passport agency is not a practical option.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Texas Tech University generates enormous passport demand. Each semester, hundreds of students depart for study abroad programs in Spain, Italy, Australia, and the United Kingdom. The university's international enrollment also includes students from over 100 countries who need passport renewals and visa support. Spring break and summer session study abroad deadlines create predictable but intense rushes that the local post offices cannot handle alone.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The cotton industry is Lubbock's economic backbone. The South Plains produces roughly 25% of America's cotton, and the Lubbock-based Plains Cotton Growers Association coordinates trade missions, international conferences, and export negotiations across Egypt, Turkey, India, and China. A Lubbock cotton executive with a trade mission to Cairo in 10 days cannot afford a 13-week post office processing timeline. We regularly process expedited passports for agricultural industry professionals who represent West Texas on the global stage.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            The West Texas oil and gas industry extends from Lubbock into the Permian Basin. Petroleum engineers, geologists, and project managers are regularly deployed to international sites — from the Middle East to West Africa to South America. These deployments often have 7–14 day notice windows. A Lubbock-based engineer receiving a field assignment to Nigeria or a contract negotiation in Dubai needs a passport with months of validity, and often needs it fast.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            Lubbock's music and cultural heritage also generates unique travel. The city is the birthplace of Buddy Holly, and the Buddy Holly Center draws international visitors and sends Lubbock musicians abroad for tours and festivals. The annual Lubbock Arts Festival and the National Ranching Heritage Center both create international exchange opportunities. The city's cultural scene, combined with its geographic isolation, makes reliable passport access essential for residents who travel for both business and artistic pursuits.
          </p>
        </div>
      </div>
    </section>
  );
}

function LubbockTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Lubbock Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">LBB — Lubbock Preston Smith International Airport</h3>
                <p className="text-gray-400 text-xs">West Texas regional hub with connections to Dallas, Houston, and Denver</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              LBB serves as the primary aviation gateway for the South Plains and eastern New Mexico. The airport offers direct flights to Dallas-Fort Worth, Houston, Denver, and Las Vegas, with connections to virtually any international destination. Lubbock's isolated geography means most international travelers connect through DFW or IAH — and passport issues discovered at the Lubbock check-in counter are harder to resolve than in larger hubs. The airport handles significant seasonal volume from Texas Tech students and agricultural industry travel.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> With no passport agency within 300 miles, Lubbock travelers must plan ahead. If you discover an expired passport within a week of travel, our courier service is your fastest option — driving to Dallas is not practical.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Texas Tech Study Abroad Programs</h3>
                <p className="text-gray-400 text-xs">Spain, Italy, Australia, UK — and growing each semester</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Texas Tech sends over 1,500 students abroad annually through programs in Spain, Italy, Australia, the United Kingdom, Costa Rica, and Japan. The Office of International Affairs coordinates deadlines that typically require valid passports 6–12 months before departure. Students who delay their application until the last minute — or who discover their passport has insufficient validity — create a predictable surge in demand that overwhelms local acceptance facilities.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Texas Tech's study abroad deadlines are typically 4–6 months before departure. Apply as soon as you are accepted — do not wait until the semester approaches. Many programs require 6+ months of validity beyond the return date.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-seedling-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Cotton Industry &amp; Agricultural Trade Missions</h3>
                <p className="text-gray-400 text-xs">West Texas cotton exports and international conferences</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The South Plains produces roughly 25% of America's cotton, and Lubbock-based industry organizations coordinate trade missions to Egypt, Turkey, India, China, and Brazil. The annual Beltwide Cotton Conferences draw international attendees, and Lubbock executives regularly travel to textile markets in Asia and Europe. These trips are often scheduled with short notice — a contract negotiation or a conference presentation can require a passport with 6 months of validity when the traveler discovers their document expires in 3.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Agricultural trade missions often require visas to China, India, and Egypt. These visas can take 2–4 weeks to process and require passport validity beyond your return date. Start the passport process before you begin visa applications.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-oil-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">West Texas Oil &amp; Gas International Deployment</h3>
                <p className="text-gray-400 text-xs">Permian Basin field assignments to global drilling sites</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The Permian Basin extends from Lubbock into New Mexico and generates significant international deployment demand. Petroleum engineers, drilling supervisors, and project managers are regularly sent to offshore platforms, pipeline installations, and refinery sites across the Middle East, West Africa, and South America. These assignments often have 7–14 day notice windows, and visas for countries like Nigeria, Angola, and the UAE require current passport validity. We process expedited passports for West Texas energy professionals monthly.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Energy sector deployments often require work visas with specific passport validity windows. A Nigerian work visa, for example, typically requires 6 months of validity beyond your contract end date. Check your assignment paperwork before you book flights.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-map-pin-2-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Lubbock's Geographic Isolation — And Why It Matters for Passports</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Lubbock is 300+ miles from Dallas and 350+ miles from Houston. The nearest passport agency is the Dallas Federal Building — a 5.5-hour drive each way. For Lubbock residents with urgent travel needs, our registered courier service eliminates the need for that drive while delivering faster processing than the standard post office channel.
            </p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function LubbockDestinationsSection() {
  const destinations = [
    { name: "Spain & Italy", context: "Texas Tech study abroad programs, academic exchange, and student travel during semester breaks", urgency: "Semester deadlines create predictable rushes in December, May, and August", icon: "ri-map-pin-2-line", flag: "ES" },
    { name: "Australia & New Zealand", context: "Texas Tech agricultural science programs, student exchange, and research collaborations", urgency: "Semester-based programs with application deadlines 4–6 months before departure", icon: "ri-map-pin-2-line", flag: "AU" },
    { name: "Egypt & Turkey", context: "Cotton industry trade missions, agricultural conferences, and textile export negotiations", urgency: "Conference schedules and trade mission dates are fixed 2–8 weeks in advance", icon: "ri-map-pin-2-line", flag: "EG" },
    { name: "Nigeria & West Africa", context: "Oil and gas field assignments, contract work, and petroleum engineering deployments", urgency: "Energy deployment orders often have 7–14 day notice windows", icon: "ri-map-pin-2-line", flag: "NG" },
    { name: "Mexico", context: "Family heritage trips, medical tourism in Juarez and Monterrey, weekend border crossings", urgency: "Spring break and summer family travel peak February through June", icon: "ri-map-pin-2-line", flag: "MX" },
    { name: "India & China", context: "Cotton and textile trade, agricultural export negotiations, and business development", urgency: "Visa processing can take 2–4 weeks, requiring passport validity well in advance", icon: "ri-map-pin-2-line", flag: "IN" },
  ];
  const commonSituations = [
    { stat: "35%", label: "of Lubbock expedited requests are Texas Tech study abroad students with semester deadlines" },
    { stat: "4–8 Weeks", label: "average notice Lubbock travelers have before international departure or trade mission" },
    { stat: "Dec–Jan", label: "peak season when spring semester study abroad and post-holiday travel collide" },
    { stat: "36 Hours", label: "shortest turnaround we have accommodated for a Lubbock cotton executive flying to Cairo" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Lubbock Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {commonSituations.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/dallas" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Dallas <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/fort-worth" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Fort Worth <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/amarillo" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Amarillo <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function LubbockTestimonialsSection() {
  const testimonials = [
    { name: "Dr. Sarah M.", location: "Texas Tech", text: "I had a research conference in Barcelona in 10 days and my passport was water damaged. Fast Passport Center processed the replacement in 3 days. I made my presentation with time to spare.", rating: 5 },
    { name: "Jake R.", location: "Wolfforth", text: "I got accepted to the study abroad program in Spain with 3 weeks to get my first passport. They walked me through every step and got it back in 7 days. I didn't miss the semester.", rating: 5 },
    { name: "The Patel Family", location: "South Lubbock", text: "We booked a heritage trip to India and realized our daughter's passport expired. They processed the renewal and got it back in 5 days. Our trip was saved.", rating: 5 },
    { name: "Marcus T.", location: "Cotton Industry", text: "I had a trade mission to Egypt in 8 days and my passport had 4 months left — not enough for the visa. They expedited the renewal in 48 hours. Professional and fast.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Lubbock Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (
                  <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>
                ))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LubbockAcceptanceFacilitiesSection() {
  const facilities = [
    { name: "Lubbock Main Post Office", address: "411 University Ave, Lubbock, TX 79401", phone: "(806) 762-8531", photo: "Yes", appointment: "Required" },
    { name: "South Loop Station Post Office", address: "5402 4th St, Lubbock, TX 79416", phone: "(806) 791-2854", photo: "Yes", appointment: "Walk-in" },
    { name: "Lubbock County Clerk", address: "904 Broadway, Lubbock, TX 79401", phone: "(806) 775-1630", photo: "No", appointment: "Required" },
    { name: "Wolfforth Post Office", address: "302 Main St, Wolfforth, TX 79382", phone: "(806) 866-3526", photo: "Yes", appointment: "Walk-in" },
    { name: "Shallowater Post Office", address: "801 Ave H, Shallowater, TX 79363", phone: "(806) 832-4441", photo: "Yes", appointment: "Required" },
    { name: "Levelland Post Office", address: "100 E State Road 114, Levelland, TX 79336", phone: "(806) 894-3161", photo: "Yes", appointment: "Required" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Lubbock Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are Lubbock-area options with hours and contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={`${f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}`}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. We recommend calling ahead to confirm availability. We can also help you find the facility with the earliest appointment slot.</p>
        </div>
      </div>
    </section>
  );
}

function LubbockComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Lubbock Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies by plan" },
                { feature: "Lubbock-Specific Guidance", us: "Yes, local facility knowledge", post: "Generic", reseller: "Generic" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function LubbockMistakesSection() {
  const mistakes = [
    { title: "Waiting Until the Last Minute", desc: "Lubbock's limited acceptance facilities mean post office appointments fill up quickly. Waiting until 2 weeks before travel often leaves you with no available slots at nearby facilities." },
    { title: "Ignoring Study Abroad Deadlines", desc: "Texas Tech students frequently miss study abroad passport deadlines by 4–6 weeks. Apply as soon as you are accepted — not when the semester approaches." },
    { title: "Unacceptable Passport Photos", desc: "Many Lubbock CVS and Walgreens photo technicians are not trained on current State Department standards. A rejected photo adds 1–2 weeks to your processing time." },
    { title: "Forgetting Parental Consent", desc: "For minors in Lubbock, both parents must appear or provide notarized consent. Single-parent applications missing the DS-3053 form are rejected outright." },
    { title: "Not Checking Passport Validity", desc: "West Texas energy and agricultural sectors book international travel with short notice. Many countries require 6 months of validity — check your expiration before booking, not after." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Lubbock Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LubbockHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our Lubbock-specific intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Lubbock-area acceptance facility. We provide the nearest location with available appointments." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application package via overnight courier to our processing center. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit your application through our registered courier channel with the State Department. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your Lubbock address via secure overnight shipping. Most Lubbock clients receive it within their chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Lubbock Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LubbockFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Lubbock Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LubbockHelpfulResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Lubbock Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <a href="https://travel.state.gov/content/travel/en/passports.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-government-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">U.S. State Department</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Official passport information, forms, and processing status updates.</p>
          </a>
          <a href="https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-earth-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Travel Advisories</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Current safety and entry requirements for every country.</p>
          </a>
          <a href="https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program" target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
              <i className="ri-visa-line text-[#1A365D] text-lg"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-sm mb-2">Visa Waiver Program</h3>
            <p className="text-gray-500 text-sm leading-relaxed">Check if your destination participates in the ESTA visa waiver program.</p>
          </a>
        </div>
      </div>
    </section>
  );
}

function LubbockFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Lubbock Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">
          Whether you are flying from LBB in 3 days or heading to a trade mission in 3 weeks, we will get your passport processed on time. No surprises. No delays. Just fast, reliable service.
        </p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function LubbockPage() {
  useEffect(() => {
    document.title = "Emergency Passport Lubbock, TX — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/lubbock/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Lubbock, TX — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Texas Tech students, cotton industry professionals, and West Texas travelers.");
    setMeta("geo.placename", "Lubbock, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "33.5779;-101.8552");
    setMeta("ICBM", "33.5779, -101.8552");
    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for Lubbock travelers with immediate departure needs." },
      { name: "Passport Renewal", description: "Fast passport renewal for Lubbock residents with recent passport issuance." },
      { name: "New Passport", description: "First-time application assistance for Lubbock adults and students." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for Lubbock residents." },
      { name: "Child Passport", description: "Passport services for minors under 16 in Lubbock." },
      { name: "Name Change", description: "Passport correction after marriage, divorce, or legal name changes." },
      { name: "Second Passport", description: "Second passport issuance for frequent Lubbock business travelers." },
    ];
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "Lubbock Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", description: "Fast Passport Center does not have a physical office in Lubbock — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Lubbock and West Texas travelers.", url: pageUrl, telephone: "+1-877-253-0084", areaServed: { "@type": "City", name: "Lubbock", containedInPlace: { "@type": "State", name: "Texas" } }, priceRange: "$$$", openingHours: ["Mo-Fr 09:00-18:00"], aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" }},
      ...servicesList.map((s) => ({ "@type": "Service", serviceType: s.name, provider: { "@type": "LocalBusiness", name: "Fast Passport Center" }, areaServed: { "@type": "City", name: "Lubbock" }, description: s.description })),
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <LubbockHeroSection />
      <LubbockStickyNav />
      <LubbockProcessingTimeSection />
      <LubbockServicesSection />
      <LubbockLocalAreaSection />
      <LubbockTravelTimingSection />
      <LubbockDestinationsSection />
      <LubbockTestimonialsSection />
      <LubbockAcceptanceFacilitiesSection />
      <LubbockComparisonSection />
      <LubbockMistakesSection />
      <LubbockHowItWorksSection />
      <LubbockFAQSection />
      <LubbockHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/lubbock" state="TX" />
      <LubbockFinalCTA />
    </div>
  );
}